import React, { useState } from 'react'
import PageLayout from 'layouts/PageLayout'
import Footer from 'components/Footer'
import Panel from 'components/Panel'
import Caption from 'components/Caption'
import Button, { LinkButton } from 'components/Button'
import { RouteParams, routes } from 'constants/routes'
import SignInForm, {
  Errors,
  Fields,
  ExtendedFormikProps,
} from 'components/SignInForm/SignInForm'
import { Flex } from 'components/Box'
import { Header } from '../LinkAccountConfirmationPage/LinkAccountConfirmationPage.styles'
import { SsoTag } from 'modules/workspace/containers/WorkspacesDropdown'
import {
  StyledPanel,
  UberHeader,
  Separator,
  StyledHeader,
  FormContainer,
  GenericErrorMessage,
} from './SignInToLinkAccountPage.styles'
import { useQueryParams } from 'utils'
import { useGetPublicWorkspaceQuery } from 'gqlTypes'
import { useParams } from 'react-router-dom'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import WorkspaceLogo from 'modules/workspace/components/WorkspaceLogo'

const SIGN_IN_FORM_ID = 'SignInForm'

export const FormFields: React.FC<ExtendedFormikProps> = formProps => (
  <>
    <Fields {...formProps} hideForgotPasswordLink />
    <Errors {...formProps} />
    <input type="submit" id={SIGN_IN_FORM_ID} hidden />
  </>
)

export const FullHeader = ({
  name,
  avatarUrl,
}: {
  name: string
  avatarUrl: string | null
}) => {
  return (
    <>
      <UberHeader>
        <Flex flexDirection="row" alignItems="center">
          <Header>Signing Into</Header>
          <WorkspaceLogo
            size="24px"
            src={avatarUrl}
            workspaceName={name}
            UNSAFE_ADD_MARGIN
          />
          <>{name}</>
          <SsoTag />
        </Flex>
      </UberHeader>
      <Separator />
      <StyledHeader>Link to an existing Sketch Account?</StyledHeader>
    </>
  )
}

export const SignInToLinkAccountPage = () => {
  const [signInLoading, setSignInLoading] = useState(false)
  const { workspaceId } = useParams<RouteParams<'SSO_LINK_ACCOUNT'>>()
  const queryParams = useQueryParams<'SSO_LINK_ACCOUNT'>()
  const { data, loading, error } = useGetPublicWorkspaceQuery({
    variables: { identifier: workspaceId },
  })

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error || !data?.publicWorkspace) {
    return <GenericErrorMessage />
  }

  return (
    <PageLayout showHeader={false} footer={<Footer />}>
      <StyledPanel>
        <FullHeader
          name={data.publicWorkspace.name}
          avatarUrl={data.publicWorkspace?.avatar?.small ?? null}
        />

        <Panel.Body>
          <Caption>
            This will help you easily switch between this new SSO workspace and
            other workspaces you already belong to.
          </Caption>

          <FormContainer>
            <SignInForm>
              {formikBag => {
                setSignInLoading(formikBag.isSubmitting)
                return <FormFields {...formikBag} />
              }}
            </SignInForm>
          </FormContainer>
        </Panel.Body>
        <Panel.Footer>
          <LinkButton
            to={routes.SSO_TOS.create({
              workspaceId,
              query: queryParams,
            })}
            variant="secondary"
            disabled={signInLoading}>
            Don&apos;t Link
          </LinkButton>
          <Button
            variant="primary"
            loading={signInLoading}
            disabled={signInLoading}>
            <label htmlFor={SIGN_IN_FORM_ID}>Link Account</label>
          </Button>
        </Panel.Footer>
      </StyledPanel>
    </PageLayout>
  )
}
